// src/utils/turnos.ts
// ============================================================================
// Turnos de CAJA (matutino / vespertino / único) derivados del horario del
// estudio. Port del motor de la agenda dental (agenda/src/utils/turnos.ts),
// sin config compilada de ningún tenant: aquí SIEMPRE se alimenta en runtime
// desde /finances/cashbox/config (setTurnosRuntime); los defaults empatan con
// los del backend (09:00–21:00, split 15:00, Lun–Sáb).
// ============================================================================

export type TipoTurno = 'matutino' | 'vespertino';

export interface FranjaTurno {
  tipo: TipoTurno;
  inicio: string; // 'HH:MM'
  fin: string;    // 'HH:MM'
}

// Tolerancia (min) de "pre-entrada": permite abrir caja un poco antes del inicio.
const TOLERANCIA_MIN = 15;

const LABEL_TURNO: Record<TipoTurno, string> = {
  matutino: 'Matutino',
  vespertino: 'Vespertino',
};

let RUNTIME_CONFIG: Record<number, FranjaTurno[]> | null = null;
let RUNTIME_SINGLE_LABEL = false; // true = días de 1 franja se etiquetan "Único"
let RUNTIME_SPLIT: string | null = null; // hora de corte matutino→vespertino (null = turno único)

export function setTurnosRuntime(
  cfg: { openingTime?: string; closingTime?: string; workingDays?: number[]; split?: string | null } | null,
): void {
  const hhmm = /^\d{2}:\d{2}$/;
  if (!cfg || !hhmm.test(cfg.openingTime || '') || !hhmm.test(cfg.closingTime || '')) {
    RUNTIME_CONFIG = null; RUNTIME_SINGLE_LABEL = false; RUNTIME_SPLIT = null;
    return;
  }
  const open = cfg.openingTime as string;
  const close = cfg.closingTime as string;
  const days = (Array.isArray(cfg.workingDays) && cfg.workingDays.length)
    ? cfg.workingDays.filter((d) => d >= 0 && d <= 6)
    : [1, 2, 3, 4, 5, 6];
  const split = cfg.split && hhmm.test(cfg.split) && cfg.split > open && cfg.split < close
    ? cfg.split : null;
  const franjas: FranjaTurno[] = split
    ? [{ tipo: 'matutino', inicio: open, fin: split }, { tipo: 'vespertino', inicio: split, fin: close }]
    : [{ tipo: 'matutino', inicio: open, fin: close }];
  const out: Record<number, FranjaTurno[]> = {};
  for (let d = 0; d <= 6; d++) out[d] = days.includes(d) ? franjas : [];
  RUNTIME_CONFIG = out;
  RUNTIME_SINGLE_LABEL = !split;
  RUNTIME_SPLIT = split;
}

/**
 * ¿Matutino o vespertino? para una caja YA abierta (23-sep-2026, sol/luna en la lista de Efectivo).
 * Las cajas que vienen de Kydemy no traen turno: se deduce de la HORA DE APERTURA contra el corte
 * configurado en Mi Estudio. Sin corte (turno único) → null, y no se pinta nada.
 */
export function turnoDeApertura(openedAt: string | null | undefined, label?: string | null): TipoTurno | null {
  if (!RUNTIME_SPLIT) return null;
  if (label && /matut/i.test(label)) return 'matutino';
  if (label && /vesp/i.test(label)) return 'vespertino';
  // 🪤 La API lo manda en UTC («2026-09-21T15:23:05.000Z» = 8:23 en Mexicali): leer la hora
  // del texto daría luna a las 8 de la mañana. Se convierte a la hora LOCAL del navegador.
  const raw = String(openedAt || '');
  let hhmm: string | undefined;
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(raw)) {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } else {
    hhmm = raw.match(/[T ](\d{2}:\d{2})/)?.[1];   // «YYYY-MM-DD HH:MM:SS» ya viene en hora local
  }
  if (!hhmm) return null;
  return hhmm < RUNTIME_SPLIT ? 'matutino' : 'vespertino';
}

// Defaults (espejo del backend CASHBOX_CONFIG_DEFAULTS) por si aún no carga la config.
setTurnosRuntime({ openingTime: '09:00', closingTime: '21:00', workingDays: [1, 2, 3, 4, 5, 6], split: '15:00' });

export interface TurnoActual {
  tipo: TipoTurno;
  label: string;
  inicio: Date; // inicio del turno HOY
  fin: Date;    // fin del turno HOY
}

const aMinutos = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const construir = (f: FranjaTurno, ref: Date): TurnoActual => {
  const inicio = new Date(ref);
  const [hi, mi] = f.inicio.split(':').map(Number);
  inicio.setHours(hi, mi, 0, 0);
  const fin = new Date(ref);
  const [hf, mf] = f.fin.split(':').map(Number);
  fin.setHours(hf, mf, 0, 0);
  const label = RUNTIME_SINGLE_LABEL ? 'Único' : LABEL_TURNO[f.tipo];
  return { tipo: f.tipo, label, inicio, fin };
};

/**
 * Turno vigente para la fecha/hora dada, o null si el día está cerrado.
 * Dentro de franja → esa; ventana de tolerancia antes del inicio → esa;
 * fuera de horario → 1ª franja (antes de abrir) o última (después de cerrar).
 */
export const getTurnoActual = (ahora: Date = new Date()): TurnoActual | null => {
  const franjas = (RUNTIME_CONFIG ?? {})[ahora.getDay()] || [];
  if (franjas.length === 0) return null; // día cerrado

  const min = ahora.getHours() * 60 + ahora.getMinutes();

  for (const f of franjas) {
    if (min >= aMinutos(f.inicio) && min < aMinutos(f.fin)) return construir(f, ahora);
  }
  for (const f of franjas) {
    if (min >= aMinutos(f.inicio) - TOLERANCIA_MIN && min < aMinutos(f.inicio)) return construir(f, ahora);
  }
  if (min < aMinutos(franjas[0].inicio)) return construir(franjas[0], ahora);
  return construir(franjas[franjas.length - 1], ahora);
};

/** 'HH:MM' → '9:00 a.m.' para mostrar rangos de turno. */
export const horaBonita = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' });
};
